import { useCallback, useEffect, useState } from 'react';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import ImageUploader from '../../components/ImageUploader';
import {
  Badge, Button, Chip, Empty, Input, Loading,
} from '../../components/Ui';
import { useToast } from '../../context/ToastContext';
import { currency, timeAgo } from '../../utils/format';
import { INSPECTION_PARTS } from '../../constants';

const NEXT = {
  requested: ['accepted', 'rejected'],
  accepted: ['pickup_assigned', 'inspection'],
  pickup_assigned: ['picked_up'],
  picked_up: ['inspection'],
  inspection: ['in_service'],
  estimate_sent: [],
  approved: ['in_service'],
  in_service: ['quality_check'],
  quality_check: ['ready'],
  ready: ['out_for_delivery', 'delivered'],
  out_for_delivery: ['delivered'],
};
const CONDITIONS = ['ok', 'attention', 'replace'];

/** The garage's work bench: jobs, inspection, estimates and the bill. */
const GarageJobsPage = () => {
  const toast = useToast();
  const [jobs, setJobs] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [panel, setPanel] = useState('');
  const [inspection, setInspection] = useState([]);
  const [estimate, setEstimate] = useState({ items: [{ label: '', amount: '', kind: 'part' }], note: '' });
  const [bill, setBill] = useState({ partsUsed: [], labour: '', serviceCharges: '', discount: '', paymentMode: 'cash', paymentStatus: 'pending' });

  const load = useCallback(() => {
    api.garage.partnerJobs({ limit: 40 })
      .then((r) => setJobs(r.data || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
    api.garage.partnerEarnings().then((r) => setEarnings(r.data)).catch(() => setEarnings(null));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (job, status) => {
    try {
      await api.garage.setStatus(job._id, { status });
      load();
    } catch (err) { toast.error(err.message, 'Could not update'); }
  };

  const openPanel = (job, which) => {
    setOpen(job._id);
    setPanel(which);
    if (which === 'inspection') {
      setInspection(INSPECTION_PARTS.map((p) => {
        const found = (job.inspection || []).find((i) => i.part === p.value);
        return {
          part: p.value,
          condition: (found && found.condition) || 'ok',
          note: (found && found.note) || '',
          photos: (found && found.photos) || [],
        };
      }));
    }
    if (which === 'bill') {
      setBill({
        partsUsed: (job.partsUsed || []).map((p) => ({ ...p, qty: String(p.qty), amount: String(p.amount) })),
        labour: String((job.bill && job.bill.labour) || ''),
        serviceCharges: String((job.bill && job.bill.serviceCharges) || ''),
        discount: String((job.bill && job.bill.discount) || ''),
        paymentMode: job.paymentMode || 'cash',
        paymentStatus: job.paymentStatus || 'pending',
      });
    }
  };

  const saveInspection = async (job) => {
    try {
      await api.garage.saveInspection(job._id, { inspection });
      setPanel('');
      load();
    } catch (err) { toast.error(err.message, 'Could not save'); }
  };

  const sendEstimate = async (job) => {
    const items = estimate.items
      .filter((i) => i.label.trim() && Number(i.amount))
      .map((i) => ({ label: i.label, amount: Number(i.amount), kind: i.kind }));
    if (!items.length) { toast.error('An estimate needs at least one part or job.', 'Add a line'); return; }
    try {
      await api.garage.sendEstimate(job._id, { items, note: estimate.note });
      setPanel('');
      setEstimate({ items: [{ label: '', amount: '', kind: 'part' }], note: '' });
      load();
    } catch (err) { toast.error(err.message, 'Could not send'); }
  };

  const saveBill = async (job) => {
    try {
      await api.garage.saveBill(job._id, {
        partsUsed: bill.partsUsed.map((p) => ({ name: p.name, qty: Number(p.qty) || 1, amount: Number(p.amount) || 0 })),
        labour: Number(bill.labour) || 0,
        serviceCharges: Number(bill.serviceCharges) || 0,
        discount: Number(bill.discount) || 0,
        paymentMode: bill.paymentMode,
        paymentStatus: bill.paymentStatus,
      });
      setPanel('');
      load();
    } catch (err) { toast.error(err.message, 'Could not save'); }
  };

  if (loading) return <Loading text="Loading jobs…" />;

  return (
    <div style={{ maxWidth: 900 }}>
      <PageHeader
        title="Service jobs"
        subtitle={earnings ? `${earnings.jobs} done · ${currency(earnings.net)} after commission` : 'Your workshop'}
        back
      />

      {!jobs.length ? (
        <Empty emoji="🔧" title="No service jobs yet" text="Bookings from customers show up here." />
      ) : jobs.map((item) => (
        <div className="card sh-sm" key={item._id} style={{ marginBottom: 14 }}>
          <div className="row-between">
            <div>
              <div className="t-title">{item.vehicleInfo && item.vehicleInfo.brand} {item.vehicleInfo && item.vehicleInfo.model}</div>
              <div className="t-tiny">
                {item.jobNo} · {(item.vehicleInfo && item.vehicleInfo.regNumber) || `${item.vehicleInfo && item.vehicleInfo.km} km`}
                {' · '}{timeAgo(item.createdAt)}
              </div>
            </div>
            <Badge value={item.status.replace(/_/g, ' ')} />
          </div>

          <div className="t-small">{item.services.map((s) => s.title).join(', ')}</div>
          <div className="t-tiny">
            {item.mode.replace(/_/g, ' ')}
            {item.scheduledSlot ? ` · ${item.scheduledSlot}` : ''}
            {item.address && item.address.line1 ? ` · ${item.address.line1}` : ''}
            {item.distanceKm != null ? ` · 📍 ${item.distanceKm} km away` : ''}
          </div>

          <div className="row" style={{ gap: 12, marginTop: 6 }}>
            <div className="t-small grow">
              {(item.address && item.address.name) || (item.user && item.user.name)} · {(item.address && item.address.phone) || (item.user && item.user.phone)}
              {item.address && item.address.forOther ? (
                <div className="t-tiny" style={{ color: 'var(--accent)', fontWeight: 700 }}>👥 Booked by {item.user && item.user.name}{item.user && item.user.phone ? ` (${item.user.phone})` : ''}</div>
              ) : null}
            </div>
            <a className="link-btn" href={`tel:${(item.address && item.address.phone) || (item.user && item.user.phone)}`}>📞 Call</a>
          </div>

          {item.problem && item.problem.text ? (
            <div className="t-small" style={{ background: 'var(--bg)', borderRadius: 8, padding: 10, marginTop: 8 }}>
              💬 {item.problem.text}
            </div>
          ) : null}
          {item.problem && (item.problem.photos || []).length ? (
            <div className="row wrap" style={{ gap: 8, marginTop: 8 }}>
              {item.problem.photos.map((p) => (
                <img key={p} src={fileUrl(p)} alt="" style={{ width: 84, height: 84, borderRadius: 8, objectFit: 'cover' }} />
              ))}
            </div>
          ) : null}
          {item.problem && item.problem.video && open === item._id ? <video src={fileUrl(item.problem.video)} controls style={{ width: '100%', maxHeight: 180, borderRadius: 12, background: '#000' }} /> : null}

          {item.estimate && item.estimate.status === 'pending' ? (
            <div className="t-small" style={{ color: 'var(--warning)', fontWeight: 700 }}>
              Waiting on the customer · {currency(item.estimate.total)}
            </div>
          ) : null}
          {item.bill && item.bill.total ? (
            <div className="t-body" style={{ fontWeight: 800 }}>Bill {currency(item.bill.total)}</div>
          ) : null}

          <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
            {(NEXT[item.status] || []).map((s) => (
              <Chip key={s} label={`→ ${s.replace(/_/g, ' ')}`} onClick={() => setStatus(item, s)} />
            ))}
            <Chip label="🔍 Inspection" onClick={() => openPanel(item, 'inspection')} />
            <Chip label="💰 Estimate" onClick={() => openPanel(item, 'estimate')} />
            <Chip label="🧾 Bill" onClick={() => openPanel(item, 'bill')} />
          </div>

          {open === item._id && panel === 'inspection' ? (
            <div className="card" style={{ background: 'var(--bg)', marginTop: 12 }}>
              <div className="t-title">Inspection</div>
              {inspection.map((row, x) => (
                <div className="row-between row-line" key={row.part}>
                  <span className="t-body">{(INSPECTION_PARTS.find((p) => p.value === row.part) || {}).label}</span>
                  <span className="row" style={{ gap: 6 }}>
                    {CONDITIONS.map((c) => (
                      <Chip
                        key={c}
                        label={c}
                        active={row.condition === c}
                        onClick={() => setInspection(inspection.map((r, y) => (y === x ? { ...r, condition: c } : r)))}
                      />
                    ))}
                  </span>
                </div>
              ))}
              {inspection.filter((r) => r.condition !== 'ok').map((row) => (
                <div key={`note-${row.part}`} style={{ marginTop: 10 }}>
                  <Input
                    label={`${(INSPECTION_PARTS.find((p) => p.value === row.part) || {}).label} — what did you find?`}
                    value={row.note}
                    onChange={(e) => setInspection(inspection.map((r) => (r.part === row.part ? { ...r, note: e.target.value } : r)))}
                  />
                  <ImageUploader
                    label="Photo evidence"
                    folder="service"
                    max={3}
                    value={row.photos}
                    onChange={(v) => setInspection(inspection.map((r) => (r.part === row.part ? { ...r, photos: v } : r)))}
                  />
                </div>
              ))}
              <Button title="Save inspection" onClick={() => saveInspection(item)} />
            </div>
          ) : null}

          {open === item._id && panel === 'estimate' ? (
            <div className="card" style={{ background: 'var(--bg)', marginTop: 12 }}>
              <div className="t-title">Estimate for extra work</div>
              {estimate.items.map((row, x) => (
                <div className="row" key={`est-${x}`} style={{ gap: 8, alignItems: 'center' }}>
                  <Input
                    className="grow"
                    placeholder="Brake pad"
                    value={row.label}
                    onChange={(e) => setEstimate({ ...estimate, items: estimate.items.map((i, y) => (y === x ? { ...i, label: e.target.value } : i)) })}
                  />
                  <Input
                    placeholder="₹"
                    inputMode="numeric"
                    value={row.amount}
                    onChange={(e) => setEstimate({ ...estimate, items: estimate.items.map((i, y) => (y === x ? { ...i, amount: e.target.value.replace(/\D/g, '') } : i)) })}
                  />
                  <Chip
                    label={row.kind}
                    onClick={() => setEstimate({
                      ...estimate,
                      items: estimate.items.map((i, y) => (y === x ? { ...i, kind: i.kind === 'part' ? 'labour' : 'part' } : i)),
                    })}
                  />
                </div>
              ))}
              <button type="button" className="link-btn" onClick={() => setEstimate({ ...estimate, items: [...estimate.items, { label: '', amount: '', kind: 'part' }] })}>
                + Add a line
              </button>
              <Input label="Note to the customer" value={estimate.note} onChange={(e) => setEstimate({ ...estimate, note: e.target.value })} />
              <Button title="Send for approval" onClick={() => sendEstimate(item)} />
            </div>
          ) : null}

          {open === item._id && panel === 'bill' ? (
            <div className="card" style={{ background: 'var(--bg)', marginTop: 12 }}>
              <div className="t-title">Bill</div>
              {bill.partsUsed.map((p, x) => (
                <div className="row" key={`part-${x}`} style={{ gap: 8, alignItems: 'center' }}>
                  <Input className="grow" placeholder="Part" value={p.name} onChange={(e) => setBill({ ...bill, partsUsed: bill.partsUsed.map((r, y) => (y === x ? { ...r, name: e.target.value } : r)) })} />
                  <Input placeholder="Qty" inputMode="numeric" value={p.qty} onChange={(e) => setBill({ ...bill, partsUsed: bill.partsUsed.map((r, y) => (y === x ? { ...r, qty: e.target.value.replace(/\D/g, '') } : r)) })} />
                  <Input placeholder="₹" inputMode="numeric" value={p.amount} onChange={(e) => setBill({ ...bill, partsUsed: bill.partsUsed.map((r, y) => (y === x ? { ...r, amount: e.target.value.replace(/\D/g, '') } : r)) })} />
                </div>
              ))}
              <button type="button" className="link-btn" onClick={() => setBill({ ...bill, partsUsed: [...bill.partsUsed, { name: '', qty: '1', amount: '' }] })}>
                + Add a part
              </button>
              <div className="split-3" style={{ gap: 12 }}>
                <Input label="Service charges" inputMode="numeric" value={bill.serviceCharges} onChange={(e) => setBill({ ...bill, serviceCharges: e.target.value.replace(/\D/g, '') })} />
                <Input label="Labour" inputMode="numeric" value={bill.labour} onChange={(e) => setBill({ ...bill, labour: e.target.value.replace(/\D/g, '') })} />
                <Input label="Discount" inputMode="numeric" value={bill.discount} onChange={(e) => setBill({ ...bill, discount: e.target.value.replace(/\D/g, '') })} />
              </div>
              <div className="row wrap" style={{ gap: 8, marginBottom: 10 }}>
                {['cash', 'upi', 'card', 'online'].map((m) => (
                  <Chip key={m} label={m} active={bill.paymentMode === m} onClick={() => setBill({ ...bill, paymentMode: m })} />
                ))}
                <Chip
                  label={bill.paymentStatus === 'paid' ? '✓ Paid' : 'Mark paid'}
                  active={bill.paymentStatus === 'paid'}
                  onClick={() => setBill({ ...bill, paymentStatus: bill.paymentStatus === 'paid' ? 'pending' : 'paid' })}
                />
              </div>
              <div className="t-tiny">18% tax is added to the total automatically.</div>
              <Button title="Save bill" onClick={() => saveBill(item)} />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default GarageJobsPage;
